import axios from "axios";
import type { ErrorEvent, EventHint } from "@sentry/react";

// Printer/QZ failures that originate in the qz-tray library or the receipt
// renderers rather than as our `PrinterError` (e.g. the printer setup page's
// test-connection / discovery flow). They're user-resolvable hardware issues,
// already surfaced via a toast, and must not pollute the error stream.
const PRINTER_NOISE =
  /unable to establish connection with qz|cannot find printer|printer config (?:is required|not provided)|qz tray|qz-tray/i;

// Uncaught errors thrown from inside the qz-tray library itself — e.g. its
// websocket onopen handler dereferencing a `_qz.websocket.connection` that a
// concurrent disconnect() already nulled. They surface via the global onerror
// handler (uncatchable by our try/catch) and carry no qz token in the message,
// only in the stack.
const QZ_TRAY_STACK = /qz-tray/i;

// qz-tray is a plain dependency with no manualChunks entry, so a production
// build folds it into one of our own /assets/ chunks and its frames carry no
// "qz-tray" path — QZ_TRAY_STACK above only ever matches in dev. Property
// names survive minification, and `websocket.connection` is qz-tray's internal
// `_qz` state: every websocket reference in our own code goes through the `qz`
// facade, never that chain (DEV-9166).
const QZ_WEBSOCKET_INTERNALS = /websocket\.connection\b/i;

// Request cancellations, aborts and timeouts that lost their axios type after
// being rethrown / normalized.
const NETWORK_NOISE = /request aborted|timeout exceeded|^canceled$/i;

// A JS/CSS chunk that failed to load, per browser, plus the MIME error a
// deleted chunk produces once the SPA fallback serves index.html in its place.
// Kept at warning: they are what a stale install looks like at startup.
const CHUNK_LOAD_FAILURE =
  /importing a module script failed|failed to fetch dynamically imported module|error loading dynamically imported module|loading (?:css )?chunk|unable to preload css|javascript mime type/i;

// React's lazy() initializer reads the resolved module's `.default`. When a
// stale or partial chunk resolves to an empty module after a deploy,
// `payload._result` is undefined and React throws while reading `.default` —
// the render-time twin of the fetch-time chunk failures above, but carrying
// none of their fetch-level messages, so CHUNK_LOAD_FAILURE never catches it.
// This runs in `beforeSend`, before Sentry applies source maps, so a frame's
// filename here is the runtime bundle URL — react/react-dom/scheduler are
// coalesced into the `vendor-react` chunk (vite.config.mts), not the
// source-mapped `react.production.js` path that only exists after server-side
// processing. Match the chunk name so the downgrade actually fires in prod;
// react-dom shares the chunk, but only React's lazy initializer produces this
// message, so the message gate is the real discriminator (DEV-10674).
const LAZY_MODULE_DEFAULT_READ =
  /reading 'default'|evaluating '[^']*\.default'/i;
const REACT_LAZY_FRAME =
  /vendor-react|react\.(?:production|development)(?:\.min)?\.js/i;

// HeroUI wraps its overlays in framer-motion's <LazyMotion> with a lazily
// imported features bundle; when that import resolves to undefined (a stale or
// failed chunk after a deploy) framer-motion destructures `renderer` off it and
// rejects. framer-motion does its own import(), so neither vite:preloadError
// nor CHUNK_LOAD_FAILURE sees it. It ships in our `vendor-framer` /assets/
// chunk, so match that chunk positively (DEV-10670).
const LAZY_MOTION_FEATURES_UNDEFINED =
  /destructure (?:the )?property 'renderer'/i;
// Safari/WebKit phrases the same destructure-of-undefined as a generic "Right
// side of assignment cannot be destructured" with no property name, so the
// 'renderer' pattern above never matches it. The framer-motion frame gate below
// keeps it specific to the LazyMotion features bundle (DEV-11280).
const WEBKIT_DESTRUCTURE_UNDEFINED =
  /right side of assignment cannot be destructured/i;
const FRAMER_MOTION_FRAME = /vendor-framer|framer-motion/i;

// Safari reports a failed dynamic import / fetch as a bare "Load failed".
const SAFARI_LOAD_FAILED = /^load failed/i;

// WebKit's generic, message-less TypeError. iOS Safari raises it from event
// handlers Sentry's browserApiErrors integration wraps — e.g. react-aria's
// scroll-lock touch listeners (usePreventScroll) on an open HeroUI overlay —
// with a misattributed third-party stack and nothing on our page to fix.
const WEBKIT_GENERIC_TYPE_ERROR = /^(?:TypeError:\s*)?Type error$/;
const BROWSER_API_ERRORS_MECHANISM = "browserapierrors";

// Browser-injected content scripts (seen from Chrome for iOS 150.0.7871 as
// `Error: fa`) throw a bare minified identifier. They carry no source URL, so
// the frame is our document at a fixed line:col that stays identical across
// every route and every release — never one of our /assets/ chunks.
const MINIFIED_TOKEN_MESSAGE = /^[a-z]{1,3}$/i;
const GLOBAL_ONERROR_MECHANISM = "onerror";
const APP_CHUNK_FRAME = /\/assets\//;

// Microsoft Outlook's SafeLink crawler pre-fetches links in emails and injects
// its own script into the page; that script raises an `unhandledrejection` with
// a non-Error value like `Object Not Found Matching Id:3, MethodName:update,
// ParamCount:4`, which Sentry captures as "Non-Error promise rejection captured
// with value: …". It has no first-party frame and nothing on our page to fix;
// the phrase is specific enough to drop without swallowing real rejections
// (DEV-9901).
const OUTLOOK_SAFELINK_NOISE = /Object Not Found Matching Id/i;

// HubSpot's autotrack (`hstc`) collects the clicked element's text and runs it
// through encodeURIComponent; a lone/unpaired surrogate in that text makes the
// native encoder throw `URIError: URI malformed`. The whole stack lives in
// HubSpot's analytics bundle (js.hs-analytics.net/analytics/<cachebuster>/
// <portalId>.js) — Sentry only sees it because its browserApiErrors integration
// wraps the addEventListener handler. There is no first-party frame and nothing
// on our page to fix (DEV-9870).
const URI_MALFORMED = /^(?:URIError:\s*)?URI malformed$/i;
const HUBSPOT_ANALYTICS_FRAME = /\/analytics\/\d+\/\d+\.js|hs-analytics/i;

// Google Tag Manager (which we inject after consent) fires marketing/ad tags
// that fetch ad domains like ad.doubleclick.net. When an ad blocker, a browser
// tracking-prevention setting, or the network blocks that request, the fetch
// rejects with a bare `TypeError: Failed to fetch`; Sentry's fetch
// instrumentation + onunhandledrejection handler capture it even though the
// request and its whole originating stack live in Google's gtm.js. There is no
// first-party frame and nothing on our page to fix (DEV-9934).
const FAILED_TO_FETCH = /^(?:TypeError:\s*)?Failed to fetch/i;
const AD_TAG_FRAME =
  /\/gtm\.js|googletagmanager|doubleclick|google-analytics|\/gtag\//i;

// The same blocked ad-tag fetch can reject with no ad-tag frame in the async
// stack: a fire-and-forget tag request that unwinds only through a fetch wrapper
// (Sentry's own instrumentation, or a browser extension's), leaving nothing on
// the stack to match AD_TAG_FRAME. Sentry's fetch instrumentation appends the
// blocked request host to the exception value, so match the Google ad/marketing
// hosts there as a second discriminator — we only load these as third-party
// tags and never fetch them from first-party code (DEV-11627).
const AD_TAG_FETCH =
  /Failed to fetch \([^)]*(?:doubleclick|googlesyndication|googleadservices|google-analytics)[^)]*\)/i;

// HubSpot's tracking bundle (injected from js-eu1.hs-scripts.com) fires its own
// analytics requests to js[-eu1].hs-analytics.net; when an ad blocker, tracking
// prevention, or the network blocks one it rejects with `TypeError: Failed to
// fetch`. Unlike the GTM/ad-tag case this rejection carries no HubSpot stack
// frame — the async fetch unwinds only through a fetch wrapper (Sentry's own
// instrumentation, or a browser extension's) — so there is nothing to match on
// the stack. Sentry's fetch instrumentation does append the blocked request URL
// to the exception value (`Failed to fetch (js-eu1.hs-analytics.net)`), so gate
// on that host instead. We only ever load HubSpot as a script and never fetch
// hs-analytics.net ourselves, so a blocked request to it is always third-party
// tracking noise (DEV-10939).
const HUBSPOT_ANALYTICS_FETCH = /Failed to fetch \([^)]*hs-analytics[^)]*\)/i;

// Sentry's own fetch instrumentation monkey-patches the global `fetch`, so its
// wrapper frame sits in the call stack of *every* fetch rejection — including
// the third-party tag fetches we don't own. It ships in our `/assets/`
// vendor-sentry chunk (vite.config.mts), so left unchecked it satisfies
// `hasAppChunkFrame` and defeats the "no first-party frame" gate on the
// third-party-noise filters below (DEV-9964). It never carries a genuine
// first-party origin, so exclude it when deciding whether an error originated
// in our own code.
const INSTRUMENTATION_FRAME = /vendor-sentry|@sentry/i;

// Browser extensions built with Vite/WXT serve their own bundle from
// chrome-extension://<id>/assets/… (moz-/safari- equivalents too), so their
// frames satisfy `APP_CHUNK_FRAME`'s `/assets/` test even though they are not
// our code. A blocked third-party fetch unwinds through such a frame when the
// user has one of these extensions installed, which trips `hasAppChunkFrame`
// and defeats the "no first-party frame" gate on the noise filters below —
// most visibly the GTM/ad-tag `Failed to fetch` rule. Exclude extension-origin
// frames the same way instrumentation frames are excluded (DEV-11349).
const BROWSER_EXTENSION_FRAME =
  /(?:chrome-extension|moz-extension|safari-web-extension|safari-extension):\/\//i;

// Facebook/Instagram's in-app browser injects its own "declutter" script at a
// pseudo-URL (`iabjs://browser_declutter`) rather than a real http(s) origin.
// That script throws its own `SyntaxError: Unexpected end of input` with no
// first-party frame and nothing on our page to fix (DEV-10460).
const IABJS_FRAME = /^iabjs:\/\//i;

// iOS in-app browsers / WKWebViews inject a bridge that dereferences
// `window.webkit.messageHandlers`; outside that host `window.webkit` is
// undefined, so it throws `TypeError: undefined is not an object (evaluating
// 'window.webkit.messageHandlers')`. It runs as page global code, so the frame
// URL is our page and denyUrls can't catch it — match on the message. Our own
// code never touches that chain, so an unconditional match drops only this
// injected-bridge noise (DEV-10610). The form app filters the same message.
const WEBKIT_MESSAGE_HANDLERS = /window\.webkit\.messageHandlers/;

// Cloudflare Turnstile schedules its own challenge retry via setTimeout; when
// the React host that mounts the widget unmounts before that timer fires — e.g.
// the login page navigating to the dashboard after a successful sign-in — the
// retry calls reset() on a container that is already gone and throws
// `[Cloudflare Turnstile] Nothing to reset found for provided container.`. The
// whole stack lives in Cloudflare's hosted api.js (Sentry only sees it through
// its browserApiErrors setTimeout integration) with no first-party frame and
// nothing on our page to fix — Cloudflare's code does not cancel the retry
// timer on remove() (DEV-11452).
const TURNSTILE_CONTAINER_RESET =
  /Nothing to reset found for provided container/i;
const TURNSTILE_FRAME = /challenges\.cloudflare\.com|\/turnstile\/v0\//i;

function hasFrameMatching(event: ErrorEvent, pattern: RegExp): boolean {
  return (event.exception?.values ?? []).some((value) =>
    (value.stacktrace?.frames ?? []).some(
      (frame) =>
        pattern.test(frame.filename ?? "") ||
        pattern.test(frame.abs_path ?? ""),
    ),
  );
}

function hasAppChunkFrame(event: ErrorEvent): boolean {
  return (event.exception?.values ?? []).some((value) =>
    (value.stacktrace?.frames ?? []).some((frame) => {
      const location = `${frame.filename ?? ""} ${frame.abs_path ?? ""}`;
      return (
        APP_CHUNK_FRAME.test(location) &&
        !INSTRUMENTATION_FRAME.test(location) &&
        !BROWSER_EXTENSION_FRAME.test(location)
      );
    }),
  );
}

/**
 * Decides which client errors reach Sentry's error stream, and at what level.
 *
 * Returns `null` to drop an event entirely (pure noise) or the event with an
 * adjusted `level` to keep it for trend visibility without counting it as an
 * error:
 * - dropped: `PrinterError`, `DevicePrinterUserError` + raw printer/QZ
 *   failures, axios cancellations,
 *   aborts/timeouts, 4xx other than 401/403 (validation / bad input the user
 *   already sees via a toast), WebKit's message-less "Type error" raised from a
 *   wrapped browser API on iOS Safari, bare minified tokens thrown by
 *   browser-injected content scripts with no frame in our own bundle,
 *   `URIError: URI malformed` thrown inside HubSpot's autotrack bundle with no
 *   frame in our own bundle, the Outlook SafeLink crawler's non-Error
 *   `Object Not Found Matching Id` promise rejection, `TypeError: Failed to
 *   fetch` from a blocked GTM/DoubleClick ad-tag request (identified by an
 *   ad-tag frame, or by the blocked ad host in the value when the rejection
 *   carries no ad-tag frame) or from a blocked HubSpot tracking request
 *   (identified by an `hs-analytics` host in the value, since it carries no
 *   HubSpot frame),
 *   any error thrown from an `iabjs://` frame injected by Facebook/Instagram's
 *   in-app browser, the
 *   `window.webkit.messageHandlers` TypeError from an iOS in-app
 *   browser / WKWebView injected bridge, and Cloudflare Turnstile's
 *   `Nothing to reset found for provided container` thrown from its own retry
 *   timer after the widget's React host unmounted (identified by a Turnstile
 *   frame with no first-party frame).
 * - kept at `warning`: chunk-load and module MIME-type failures, and React's
 *   lazy() initializer throwing `reading 'default'` when a stale chunk resolves
 *   to an empty module after a deploy, and framer-motion's LazyMotion
 *   destructuring off a features bundle that failed to load (Chrome/Firefox's
 *   `'renderer'` wording and Safari's generic "Right side of assignment cannot
 *   be destructured").
 * - kept at `info`: 401/403 (expected, but a spike signals an auth/permission
 *   outage), 5xx (real server failures owned by the server Sentry project —
 *   info avoids double-counting while preserving correlation) and Safari's
 *   "Load failed" (almost always a stale chunk after a deploy).
 */
export function beforeSendFilter(
  event: ErrorEvent,
  hint?: EventHint,
): ErrorEvent | null {
  const error = hint?.originalException;
  const message = error instanceof Error ? error.message : "";
  // Global-onerror events often arrive without a usable `originalException`,
  // so fall back to the exception value Sentry parsed off the event.
  const primaryException = event.exception?.values?.[0];
  const mechanismType = primaryException?.mechanism?.type ?? "";
  const exceptionValue = message || primaryException?.value || "";

  // Printer/QZ failures are expected, user-resolvable, already logged at
  // warning level + shown via a toast — don't report as errors. Match our
  // wrapped PrinterError, the device-printer user error thrown pre-flight by
  // preparePrintData (before printReceipt can rewrap it), and the unwrapped
  // library/renderer messages.
  if (
    error instanceof Error &&
    (error.name === "PrinterError" || error.name === "DevicePrinterUserError")
  ) {
    return null;
  }
  if (PRINTER_NOISE.test(message)) return null;
  if (error instanceof Error && QZ_TRAY_STACK.test(error.stack ?? "")) {
    return null;
  }
  if (QZ_WEBSOCKET_INTERNALS.test(exceptionValue)) return null;

  if (OUTLOOK_SAFELINK_NOISE.test(exceptionValue)) return null;

  if (WEBKIT_MESSAGE_HANDLERS.test(exceptionValue)) return null;

  // Facebook's in-app browser injecting its own declutter script — drop only
  // when the stack has an iabjs:// frame and no frame comes from our own
  // bundle, so a genuine first-party error still reports.
  if (hasFrameMatching(event, IABJS_FRAME) && !hasAppChunkFrame(event)) {
    return null;
  }

  // Cloudflare Turnstile's post-unmount retry resetting a gone container — drop
  // only when a Turnstile frame is present and no frame comes from our own
  // bundle, so a genuine first-party error still reports.
  if (
    TURNSTILE_CONTAINER_RESET.test(exceptionValue) &&
    hasFrameMatching(event, TURNSTILE_FRAME) &&
    !hasAppChunkFrame(event)
  ) {
    return null;
  }

  // Drop WebKit's uncatchable "Type error" only when it came through a wrapped
  // browser API (touch/scroll listeners on iOS Safari). Gate on the mechanism so
  // a genuine, descriptively-messaged TypeError from our own code still reports.
  if (
    mechanismType.includes(BROWSER_API_ERRORS_MECHANISM) &&
    WEBKIT_GENERIC_TYPE_ERROR.test(exceptionValue)
  ) {
    return null;
  }

  // Gate on the absence of any frame from our own bundle so a genuine minified
  // error thrown from an /assets/ chunk still reports.
  if (
    mechanismType.includes(GLOBAL_ONERROR_MECHANISM) &&
    MINIFIED_TOKEN_MESSAGE.test(exceptionValue) &&
    !hasAppChunkFrame(event)
  ) {
    return null;
  }

  // HubSpot autotrack's encodeURIComponent choking on a malformed surrogate in
  // tracked element text — drop only when a HubSpot analytics frame is present
  // and no frame comes from our own bundle, so a genuine first-party URIError
  // still reports.
  if (
    URI_MALFORMED.test(exceptionValue) &&
    hasFrameMatching(event, HUBSPOT_ANALYTICS_FRAME) &&
    !hasAppChunkFrame(event)
  ) {
    return null;
  }

  // GTM/DoubleClick ad-tag fetch blocked by an ad blocker or tracking
  // prevention — drop when the ad tag is identified either by an ad-tag frame
  // in the stack or by the blocked ad host Sentry appends to the value (the
  // rejection can carry no ad-tag frame when the tag fired and forgot the
  // request), and no frame comes from our own bundle, so a genuine first-party
  // `Failed to fetch` that happens to share the stack with a blocked ad-tag
  // request still reports. Gate on `hasAppChunkFrame`, not
  // `hasFrameMatching(..., APP_CHUNK_FRAME)`: it already excludes
  // `INSTRUMENTATION_FRAME` (vendor-sentry) and `BROWSER_EXTENSION_FRAME`, so
  // Sentry's own fetch instrumentation and a user's browser extension no longer
  // trip the guard on every fetch-originated rejection (DEV-9934, DEV-9964,
  // DEV-11349, DEV-11627).
  if (
    FAILED_TO_FETCH.test(exceptionValue) &&
    (hasFrameMatching(event, AD_TAG_FRAME) ||
      AD_TAG_FETCH.test(`${message} ${primaryException?.value ?? ""}`)) &&
    !hasAppChunkFrame(event)
  ) {
    return null;
  }

  // HubSpot tracking fetch to hs-analytics.net blocked by an ad blocker or
  // tracking prevention — identified by the request host Sentry appends to the
  // value, since the rejection carries no HubSpot frame. The host only ever
  // appears in the parsed exception value, so check it alongside the message;
  // gate on the absence of an app-chunk frame so a genuine first-party
  // `Failed to fetch` still reports.
  if (
    HUBSPOT_ANALYTICS_FETCH.test(
      `${message} ${primaryException?.value ?? ""}`,
    ) &&
    !hasAppChunkFrame(event)
  ) {
    return null;
  }

  if (axios.isCancel(error)) return null;

  if (axios.isAxiosError(error)) {
    if (error.code === "ECONNABORTED" || error.code === "ERR_CANCELED")
      return null;

    const status = error.response?.status;
    if (status !== undefined) {
      // Expected, but worth keeping at info: a spike of 401/403 signals an
      // auth/permission outage rather than a per-user mistake.
      if (status === 401 || status === 403) {
        event.level = "info";
        return event;
      }
      // Other 4xx are user-facing validation/input errors already toasted.
      if (status >= 400 && status < 500) return null;
      // 5xx are real server failures — keep at info for correlation only.
      if (status >= 500) {
        event.level = "info";
        return event;
      }
    }
  }

  if (CHUNK_LOAD_FAILURE.test(exceptionValue)) {
    event.level = "warning";
    return event;
  }

  if (
    LAZY_MODULE_DEFAULT_READ.test(exceptionValue) &&
    hasFrameMatching(event, REACT_LAZY_FRAME)
  ) {
    event.level = "warning";
    return event;
  }

  if (
    (LAZY_MOTION_FEATURES_UNDEFINED.test(exceptionValue) ||
      WEBKIT_DESTRUCTURE_UNDEFINED.test(exceptionValue)) &&
    hasFrameMatching(event, FRAMER_MOTION_FRAME)
  ) {
    event.level = "warning";
    return event;
  }

  // Stale chunk after a deploy, but can also be a genuine resource failure —
  // keep at info rather than drop.
  if (SAFARI_LOAD_FAILED.test(message)) {
    event.level = "info";
    return event;
  }

  // Errors that lost their axios type (e.g. rethrown / normalized).
  if (NETWORK_NOISE.test(message)) return null;

  return event;
}
